import {
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    AlignmentType,
    VerticalAlign,
    HeightRule,
    TableLayoutType,
    BorderStyle,
    ImageRun,
    HorizontalPositionRelativeFrom,
    VerticalPositionRelativeFrom,
    TextWrappingType,
    TextWrappingSide,
    HorizontalPositionAlign,
    VerticalPositionAlign
} from "docx";
import { fixColor, fitImage, fetchImageWithDimensions, FULL_WIDTH, SCALE, hexToRGBA } from "@/utils/wordGenerator/common.js";

// Helper to generate a semi-transparent overlay image
const generateOverlayImage = async (width, height, color, opacity) => {
    return new Promise((resolve) => {
        try {
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = hexToRGBA(color, opacity);
            ctx.fillRect(0, 0, width, height);
            canvas.toBlob((blob) => { blob.arrayBuffer().then(buf => resolve(new Uint8Array(buf))); });
        } catch (e) { resolve(null); }
    });
};

export const createLPTemp2 = async (lpData) => {
    const style = lpData.style || {};
    const PAGE_HEIGHT = 16838;
    const SPACER_HEIGHT = 8000;
    const CONTENT_HEIGHT = PAGE_HEIGHT - SPACER_HEIGHT;
    const bodyColor = fixColor(style.colorScheme?.body || "#f0f0f0");

    // 1. Fetch Background Image (Project 0)
    let bgImgRun = null;
    if (lpData.projects && lpData.projects[0] && (lpData.projects[0].uploadedPhoto || lpData.projects[0].photoURL)) {
        const imgData = await fetchImageWithDimensions(lpData.projects[0].uploadedPhoto || lpData.projects[0].photoURL);
        if (imgData) {
            bgImgRun = fitImage(imgData, FULL_WIDTH, PAGE_HEIGHT, "fill");
            if (bgImgRun) {
                // Manually add floating properties since fitImage returns a standard ImageRun
                bgImgRun.options.floating = {
                    zIndex: 0,
                    horizontalPosition: {
                        relative: HorizontalPositionRelativeFrom.PAGE,
                        align: HorizontalPositionAlign.CENTER,
                    },
                    verticalPosition: {
                        relative: VerticalPositionRelativeFrom.PAGE,
                        align: VerticalPositionAlign.TOP,
                    },
                    behindDocument: true,
                    wrap: { type: TextWrappingType.BEHIND }
                };
            }
        }
    }

    // 2. Generate Overlay (Black 30% opacity)
    let overlayRun = null;
    const overlayData = await generateOverlayImage(800, 800, "#000000", 0.3);
    if (overlayData) {
        overlayRun = new ImageRun({
            data: overlayData,
            transformation: { width: FULL_WIDTH / 15, height: PAGE_HEIGHT / 15 },
            floating: {
                zIndex: 1, // Above bg, below text
                horizontalPosition: {
                    relative: HorizontalPositionRelativeFrom.PAGE,
                    align: HorizontalPositionAlign.CENTER,
                },
                verticalPosition: {
                    relative: VerticalPositionRelativeFrom.PAGE,
                    align: VerticalPositionAlign.TOP,
                },
                behindDocument: true,
                wrap: { type: TextWrappingType.NONE }
            }
        });
    }

    // Helper for Text styling
    const createText = (text, styleObj, defaultSize = 12) => {
        return new TextRun({
            text: text || "N/A",
            font: styleObj?.font || "Arial",
            size: (styleObj?.size || defaultSize) * SCALE,
            bold: styleObj?.bold,
            italics: styleObj?.italic,
            underline: styleObj?.underline,
            color: fixColor(styleObj?.color || "#FFFFFF") // Default white for overlay
        });
    };

    const mainTable = new Table({
        width: { size: FULL_WIDTH, type: WidthType.DXA },
        layout: TableLayoutType.FIXED,
        borders: {
            top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
            left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
            insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
        },
        rows: [
            // Spacer Row (Holds background images)
            new TableRow({
                height: { value: SPACER_HEIGHT, rule: HeightRule.EXACT },
                children: [
                    new TableCell({
                        width: { size: FULL_WIDTH, type: WidthType.DXA },
                        shading: { fill: bodyColor },
                        children: [
                            new Paragraph({
                                children: [
                                    ...(bgImgRun ? [bgImgRun] : []),
                                    ...(overlayRun ? [overlayRun] : [])
                                ]
                            })
                        ]
                    })
                ]
            }),
            // Content Row
            new TableRow({
                height: { value: CONTENT_HEIGHT, rule: HeightRule.AT_LEAST },
                children: [
                    new TableCell({
                        width: { size: FULL_WIDTH, type: WidthType.DXA },
                        shading: { fill: bodyColor },
                        // Transparent shading
                        verticalAlign: VerticalAlign.TOP,
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                spacing: { after: 200 },
                                children: [createText(lpData.title, style.heading, 24)]
                            }),
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                spacing: { after: 100 },
                                children: [createText(lpData.name, style.label, 18)]
                            }),
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [createText(lpData.address, style.text, 12)]
                            }),
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [createText(`${lpData.city || 'N/A'}, ${lpData.stateProvince || 'N/A'}`, style.text, 12)]
                            }),
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [createText(lpData.contactNo, style.text, 12)]
                            }),
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [createText(lpData.email, style.text, 12)]
                            })
                        ]
                    })
                ]
            })
        ]
    });

    return { properties: { page: { margin: { top: 0, right: 0, bottom: 0, left: 0 } } }, children: [mainTable] };
};
